/**
 * @file CommandSearch.tsx
 * @description Global module search palette triggered by Ctrl+K / ⌘K.
 *              Searches all sidebar nav items and navigates on selection.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Lock } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { getFlatNavItems, type NavItem } from './nav-items';
import { commandSearchFilter } from './command-search-filter';
import { cn } from '@/lib/utils';
import useUIStore from '@/store/uiStore';
import { PinVerificationModal } from '@/components/common/PinVerificationModal';

const FLAT_ITEMS = getFlatNavItems();

// Group items by their root group label (e.g. Roles & Permissions, School App, Maintenance)
const grouped = FLAT_ITEMS.reduce<Record<string, typeof FLAT_ITEMS>>((acc, item) => {
  const key = item.rootGroup ?? item.group ?? 'Quick Access';
  if (!acc[key]) acc[key] = [];
  acc[key].push(item);
  return acc;
}, {});

interface CommandSearchProps {
  /** Controlled open state — pass if you want to trigger externally */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CommandSearch = ({ open: controlledOpen, onOpenChange }: CommandSearchProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const navigate = useNavigate();
  const { unlockedPaths, unlockPath } = useUIStore();

  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [pendingNav, setPendingNav] = useState<{ path: string; label: string } | null>(null);

  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback(
    (val: boolean) => {
      setInternalOpen(val);
      onOpenChange?.(val);
    },
    [onOpenChange],
  );

  // Keyboard shortcut — Ctrl+K / ⌘K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(!open);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, setOpen]);

  const handleSelect = (item: NavItem) => {
    if (item.externalUrl) {
      setOpen(false);
      window.open(item.externalUrl, '_blank');
      return;
    }
    if (!item.path) return;
    const isLocked = item.isPrivate && !unlockedPaths[item.path];
    if (isLocked) {
      setPendingNav({ path: item.path, label: item.label });
      setIsPinModalOpen(true);
      setOpen(false);
    } else {
      setOpen(false);
      navigate(item.path);
    }
  };

  const handlePinSuccess = () => {
    if (pendingNav) {
      unlockPath(pendingNav.path);
      setIsPinModalOpen(false);
      navigate(pendingNav.path);
      setPendingNav(null);
    }
  };

  return (
    <>
      <CommandDialog open={open} onOpenChange={setOpen} filter={commandSearchFilter}>
        <CommandInput placeholder="Search modules, apps, permissions… (e.g. school roles)" />
        <CommandList className="max-h-[380px] p-1">
          <CommandEmpty>
            <div className="flex flex-col items-center gap-2 py-6 text-muted-foreground">
              <Search size={32} className="opacity-30" />
              <p className="text-sm">No modules found.</p>
            </div>
          </CommandEmpty>

          {Object.entries(grouped).map(([groupName, items]) => (
            <CommandGroup key={groupName} heading={groupName}>
              {items.map((item) => {
                const Icon = item.icon;
                const isLocked = item.path ? item.isPrivate && !unlockedPaths[item.path] : false;
                const showParentBadge =
                  item.parentGroup &&
                  item.parentGroup !== item.label &&
                  item.parentGroup !== item.rootGroup;

                return (
                  <CommandItem
                    key={item.path || item.externalUrl || item.label}
                    value={
                      item.searchString ||
                      `${item.label} ${item.parentGroup ?? ''} ${item.breadcrumb ?? ''} ${item.rootGroup ?? ''} ${item.path ?? ''}`
                    }
                    keywords={item.keywords}
                    onSelect={() => handleSelect(item)}
                    className="flex items-center gap-3 cursor-pointer py-2.5 px-3 rounded-lg"
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                        'bg-primary/10 text-primary',
                      )}
                    >
                      <Icon size={16} />
                    </span>
                    <div className="flex flex-1 min-w-0 flex-col gap-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground">{item.label}</span>
                        {showParentBadge && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
                            {item.parentGroup}
                          </span>
                        )}
                        {isLocked && (
                          <Lock size={12} className="text-muted-foreground/70 shrink-0" />
                        )}
                      </div>
                      {item.breadcrumb && (
                        <span className="text-[11px] text-muted-foreground truncate">
                          {item.breadcrumb}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>

      <PinVerificationModal
        isOpen={isPinModalOpen}
        onClose={() => {
          setIsPinModalOpen(false);
          setPendingNav(null);
        }}
        onSuccess={handlePinSuccess}
      />
    </>
  );
};

export default CommandSearch;
